import { c as _c } from "react/compiler-runtime";
import { type MultiSelectDropdownItem } from '@precis-digital/kurama/MultiSelectDropdown';
import { useQueryParameterState } from 'shared/hooks/useQueryParameterState';
import { type Metric } from 'src/home/reportTemplateParameters';
import { type ProgressOverTimeChartAggregation } from 'src/home/components/utils';
import { z } from 'zod';
const MINIMUM_METRICS = 1;
const MAXIMUM_METRICS = 2;
const MINIMUM_BREAKDOWN = 0;
const MAXIMUM_BREAKDOWN = 6;
const AGGREGATION_SETTING = ['daily', 'cumulative'] as const;
export interface HomeProgressOverTimeChartQueryState<TMetric extends Metric = Metric> {
  metrics: TMetric[];
  setMetrics: (value: TMetric[]) => void;
  channels: MultiSelectDropdownItem[];
  setChannels: (value: MultiSelectDropdownItem[]) => void;
  markets: MultiSelectDropdownItem[];
  setMarkets: (value: MultiSelectDropdownItem[]) => void;
  aggregation: ProgressOverTimeChartAggregation;
  setAggregation: (value: ProgressOverTimeChartAggregation) => void;
  isComparisonMode: boolean;
  setIsComparisonMode: (value: boolean) => void;
}
export const useHomeProgressOverTimeChartQueryState = () => {
  const $ = _c(18);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["conversionValue" as TMetric];
    t1 = {
      schema: z.array(z.custom()).min(MINIMUM_METRICS).max(MAXIMUM_METRICS)
    };
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  const [metrics, setMetrics] = useQueryParameterState("ts.metrics", t0, t1);
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    t3 = {
      schema: z.array(z.custom()).min(MINIMUM_BREAKDOWN).max(MAXIMUM_BREAKDOWN)
    };
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  const [channels, setChannels] = useQueryParameterState("ts.channels", t2, t3);
  let t4;
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [];
    t5 = {
      schema: z.array(z.custom()).min(MINIMUM_BREAKDOWN).max(MAXIMUM_BREAKDOWN)
    };
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  const [markets, setMarkets] = useQueryParameterState("ts.markets", t4, t5);
  const [isComparisonMode, setIsComparisonMode] = useQueryParameterState("ts.comparison", false);
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      schema: z.enum(AGGREGATION_SETTING)
    };
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const [aggregation, setAggregation] = useQueryParameterState("ts.agg", "daily", t6);
  let t7;
  if ($[7] !== aggregation || $[8] !== channels || $[9] !== isComparisonMode || $[10] !== markets || $[11] !== metrics || $[12] !== setAggregation || $[13] !== setChannels || $[14] !== setIsComparisonMode || $[15] !== setMarkets || $[16] !== setMetrics) {
    t7 = {
      metrics,
      setMetrics,
      channels,
      setChannels,
      markets,
      setMarkets,
      aggregation,
      setAggregation,
      isComparisonMode,
      setIsComparisonMode
    };
    $[7] = aggregation;
    $[8] = channels;
    $[9] = isComparisonMode;
    $[10] = markets;
    $[11] = metrics;
    $[12] = setAggregation;
    $[13] = setChannels;
    $[14] = setIsComparisonMode;
    $[15] = setMarkets;
    $[16] = setMetrics;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  return t7;
};